import { useEffect } from 'react';
import { Text } from '@eo-locale/react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Chevron from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import { useWidget, styled } from '@guest-widgets/core';
import { classes } from '@guest-widgets/shared/src/classes';
import { Feature, SharedFeature } from '@guest-widgets/shared/src/features';
import { Booking as BookingComponent } from '@guest-widgets/shared/src/components/Booking';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { useDisableScroll } from '@guest-widgets/shared/src/components/Drawer/useDisableScroll';
import { useNumberFormatter } from '@guest-widgets/shared/src/hooks/useNumberFormatter';
import { useModalActivator } from '@guest-widgets/shared/src/hooks/useModalActivator';
import { ProductsTypeEnum } from '@guest-widgets/shared/src/types/product/baseProduct';
import {
  BOOK_MOBILE_BUTTON_HEIGHT_FACTOR,
  calcBorderRadiusFromShape,
} from '@guest-widgets/shared/src/utils/themeCalculator';

import { useProduct } from '../productContext';
import { useSettings } from '../SettingsContext/settingsContext';

const Booking = () => {
  const {
    customerCode,
    locale,
    subId,
    configuration,
    productId,
    enableFeatures,
    disableFeatures,
    productType,
  } = useSettings();
  const { isEnabled } = useFeature();
  const { widgetArea } = useWidget();
  const { value: product } = useProduct().product;
  const { formatPrice } = useNumberFormatter(product.company?.locale?.language);
  const { isOpen, onClose, onOpen } = useModalActivator();

  useDisableScroll(widgetArea.basedOnSize(isOpen, false));
  useEffect(() => {
    widgetArea.isSmall
      ? window.addEventListener('popstate', onClose)
      : window.removeEventListener('popstate', onClose);

    return () => {
      window.removeEventListener('popstate', onClose);
    };
  }, [widgetArea.isSmall]);

  const handleGoBack = () => {
    // Dispatch custom event to be listened in Booking
    const goBackEvent = new CustomEvent('product-details-go-back', {
      bubbles: true,
      composed: true, // This allows the event to cross shadow DOM boundaries
      cancelable: true, // Make the event cancelable
    });

    const canNavigate = window.dispatchEvent(goBackEvent);

    if (canNavigate) {
      onClose();
    }
  };

  const bookingComponent = (
    <BookingComponent
      customerCode={customerCode}
      locale={locale}
      subId={subId}
      configuration={configuration}
      productId={productId}
      enableFeatures={enableFeatures}
      disableFeatures={disableFeatures}
      productType={productType}
    />
  );

  const mobileBooking = (
    <>
      {!isOpen && (
        <SelectDateAndBook
          className={classes.details.booking.mobileButton}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disableElevation
          onClick={onOpen}
        >
          <Text
            id={`${
              productType === ProductsTypeEnum.GIFTCARD
                ? 'mobile-giftcard-button-label'
                : 'product-details.booking.select-date-and-book'
            }`}
          />
        </SelectDateAndBook>
      )}
      {isOpen && (
        <StyledDrawerBottom
          className={`${classes.details.drawer.root} ${classes.details.booking.drawer}`}
          anchor="bottom"
          open={isOpen}
          onClose={onClose}
          keepMounted={true}
          // See 'Re-enable "enforced focus" for filters and booking drawers' in TECHDEBT.md          disableEnforceFocus={isEnabled(SharedFeature.ShadowDom)}
          disableEnforceFocus={isEnabled(SharedFeature.ShadowDom)}
        >
          <DrawerHeader className={classes.details.drawer.header}>
            <IconButton data-cy="drawer-close-button" onClick={handleGoBack} size="large">
              <BackIcon />
            </IconButton>
            <Typography component="h2" variant="h4">
              <Text id="product-details.booking.title" />
            </Typography>
          </DrawerHeader>
          <div className={classes.details.drawer.content}>{bookingComponent}</div>
        </StyledDrawerBottom>
      )}
    </>
  );
  const desktopBooking = (
    <div>
      {product.price.gross.amount && isEnabled(Feature.ProductDetailsPrice) && (
        <Header className={classes.details.booking.header}>
          {/* In case of price is null we should not show price as zero. */}

          <Price className={classes.details.booking.price}>
            <PriceLabel className={classes.details.booking.priceLabel}>
              <Text id="product-details.booking.price-from" />
            </PriceLabel>
            <PriceValue className={classes.details.booking.priceValue}>
              {formatPrice(
                parseFloat(product.price.gross.amount),
                product.price.gross.currencyCode
              )}
            </PriceValue>
          </Price>
        </Header>
      )}
      {bookingComponent}
    </div>
  );

  return widgetArea.basedOnSize(mobileBooking, desktopBooking);
};

const Header = styled('div')(({ theme, widgetArea }) => ({
  display: 'flex',
  padding: widgetArea.basedOnSize(0, theme.spacing(4, 0), theme.spacing(4, 0, 7)),
}));

const PriceLabel = styled('span')(({ theme: { typography }, widgetArea }) => ({
  fontSize: widgetArea.basedOnSize(
    typography.h6.fontSize,
    typography.h6.fontSize,
    typography.h5.fontSize
  ),
}));

const PriceValue = styled((props) => <Typography variant="h6" component="h2" {...props} />)(
  ({ theme: { typography }, widgetArea }) => ({
    lineHeight: '1em',
    fontSize: widgetArea.basedOnSize(
      typography.h4.fontSize,
      typography.h4.fontSize,
      typography.h2.fontSize
    ),
  })
);

const Price = styled('div')({
  width: '100%',
  textAlign: 'right',
});

const SelectDateAndBook = styled(Button)(({ theme: { spacing, palette, shape } }) => ({
  borderRadius: calcBorderRadiusFromShape(BOOK_MOBILE_BUTTON_HEIGHT_FACTOR, shape.borderRadius),
  paddingTop: '0.85em',
  paddingBottom: '0.85em',
  marginTop: spacing(4),
  color: palette.background.paper,
}));

const StyledDrawerBottom = styled(Drawer)({
  '& [class*="MuiDrawer-paper"]': {
    height: '100%',
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 4, 1, 0),
  borderBottom: theme.border,
  marginBottom: theme.spacing(4),
}));

const BackIcon = styled(Chevron)({
  transform: 'rotate(180deg)',
});

export default Booking;
